import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight, Check, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import LegalFooter from "@/components/legal-footer";
import LanguageSwitcher from "@/components/language-switcher";
import {
  PUBLIC_PLANS,
  PUBLIC_CREDIT_PACKS,
  priceHtFromTtc,
  formatEur,
} from "@/lib/pricing-data";

/** 2 mois offerts = facturation sur 10 mois pour 12 */
const ANNUAL_FACTOR = 10 / 12;

function annualMonthlyPrice(monthly: number): number {
  if (monthly === 0) return 0;
  return Math.round(monthly * ANNUAL_FACTOR * 100) / 100;
}

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const planLabels: Record<string, { label: string; tagline: string; cta: string; features: string[] }> = {
    free: {
      label: t("plans.free_label"),
      tagline: t("plans.free_tagline"),
      cta: t("plans.free_cta"),
      features: [t("plans.free_f1"), t("plans.free_f2"), t("plans.free_f3"), t("plans.free_f4"), t("plans.free_f5")],
    },
    pro: {
      label: t("plans.pro_label"),
      tagline: t("plans.pro_tagline"),
      cta: t("plans.pro_cta"),
      features: [t("plans.pro_f1"), t("plans.pro_f2"), t("plans.pro_f3"), t("plans.pro_f4"), t("plans.pro_f5"), t("plans.pro_f6")],
    },
    unlimited: {
      label: t("plans.unlimited_label"),
      tagline: t("plans.unlimited_tagline"),
      cta: t("plans.unlimited_cta"),
      features: [t("plans.unlimited_f1"), t("plans.unlimited_f2"), t("plans.unlimited_f3"), t("plans.unlimited_f4"), t("plans.unlimited_f5")],
    },
  };

  const translatedFaq = [
    { q: t("faq_items.q1"), a: t("faq_items.a1") },
    { q: t("faq_items.q2"), a: t("faq_items.a2") },
    { q: t("faq_items.q3"), a: t("faq_items.a3") },
    { q: t("faq_items.q4"), a: t("faq_items.a4") },
    { q: t("faq_items.q5"), a: t("faq_items.a5") },
    { q: t("faq_items.q6"), a: t("faq_items.a6") },
    { q: t("faq_items.q7"), a: t("faq_items.a7") },
    { q: t("faq_items.q8"), a: t("faq_items.a8") },
    { q: t("faq_items.q9"), a: t("faq_items.a9") },
  ];

  const comparisonRows: Array<{ label: string; values: [string, string, string] }> = [
    {
      label: i18n.language === "en" ? "AI credits / month" : "Crédits IA inclus / mois",
      values: ["50", "1 000", i18n.language === "en" ? "Unlimited" : "Illimités"],
    },
    {
      label: i18n.language === "en" ? "Connected mailboxes" : "Boîtes mail connectées",
      values: ["1", i18n.language === "en" ? "Unlimited" : "Illimitées", i18n.language === "en" ? "Unlimited" : "Illimitées"],
    },
    {
      label: i18n.language === "en" ? "Tasks, projects, capture" : "Tâches, projets, capture",
      values: [i18n.language === "en" ? "Unlimited" : "Illimités", i18n.language === "en" ? "Unlimited" : "Illimités", i18n.language === "en" ? "Unlimited" : "Illimités"],
    },
    {
      label: i18n.language === "en" ? "Automatic email sorting" : "Tri automatique des mails",
      values: [i18n.language === "en" ? "Included" : "Inclus", i18n.language === "en" ? "Included" : "Inclus", i18n.language === "en" ? "Included" : "Inclus"],
    },
    {
      label: i18n.language === "en" ? "Conversational assistant" : "Assistant conversationnel",
      values: [i18n.language === "en" ? "Limited" : "Limité", i18n.language === "en" ? "Full" : "Complet", i18n.language === "en" ? "Full" : "Complet"],
    },
    {
      label: i18n.language === "en" ? "Billing history" : "Historique de facturation",
      values: ["—", i18n.language === "en" ? "Included" : "Inclus", i18n.language === "en" ? "Included" : "Inclus"],
    },
    {
      label: i18n.language === "en" ? "Support" : "Support",
      values: [i18n.language === "en" ? "Community" : "Communauté", i18n.language === "en" ? "Email 48 h" : "E-mail 48 h", i18n.language === "en" ? "Priority 24 h" : "Prioritaire 24 h"],
    },
    {
      label: i18n.language === "en" ? "Commitment" : "Engagement",
      values: [i18n.language === "en" ? "None" : "Aucun", i18n.language === "en" ? "None" : "Aucun", i18n.language === "en" ? "None" : "Aucun"],
    },
  ];

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col">
      <header className="border-b">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> {t("pricing_page.back_home")}
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <div className="w-6 h-6 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs">
              O
            </div>
            OneNext
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link href="/sign-in">
              <Button variant="ghost" size="sm">
                {t("nav.login")}
              </Button>
            </Link>
            <Link href="/sign-up">
              <Button size="sm" className="gap-1.5">
                {t("nav.start")}
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="px-6 pt-16 pb-10 text-center">
          <p className="text-xs uppercase tracking-widest text-primary font-semibold">
            {t("pricing_page.eyebrow")}
          </p>
          <h1 className="mt-3 text-4xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
            {t("pricing_page.title")}
          </h1>
          <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("pricing_page.subtitle")}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("pricing_page.vat_note")}
          </p>

          {/* ── Billing period toggle ── */}
          <div className="mt-8 inline-flex items-center gap-1 rounded-full border bg-muted/40 p-1">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
                billing === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {i18n.language === "en" ? "Monthly" : "Mensuel"}
            </button>
            <button
              onClick={() => setBilling("annual")}
              className={`relative px-5 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billing === "annual"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {i18n.language === "en" ? "Annual" : "Annuel"}
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300 rounded-full px-1.5 py-0.5">
                <Zap className="w-2.5 h-2.5" />
                {i18n.language === "en" ? "–17%" : "–17%"}
              </span>
            </button>
          </div>
          {billing === "annual" && (
            <p className="mt-2 text-xs text-muted-foreground">
              {i18n.language === "en"
                ? "Billed annually — 2 months free"
                : "Facturé en une fois — 2 mois offerts"}
            </p>
          )}
        </section>

        {/* Plans */}
        <section className="px-6 pb-12">
          <div className="mx-auto max-w-6xl grid md:grid-cols-3 gap-5">
            {PUBLIC_PLANS.map((p) => {
              const translated = planLabels[p.id] ?? { label: p.label, tagline: p.tagline, cta: p.cta, features: p.features };
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`relative flex flex-col rounded-2xl border bg-card p-6 ${
                    p.highlight
                      ? "border-primary shadow-xl shadow-primary/10 ring-1 ring-primary/20"
                      : ""
                  }`}
                >
                  {p.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 shadow-sm">
                      <Sparkles className="w-3 h-3" />
                      {t("pricing_section.recommended")}
                    </div>
                  )}
                  <h2 className="text-xl font-semibold tracking-tight">{translated.label}</h2>
                  <p className="mt-1 text-sm text-muted-foreground min-h-[2.5rem]">
                    {translated.tagline}
                  </p>
                  {(() => {
                    const displayPrice = billing === "annual"
                      ? annualMonthlyPrice(p.monthlyPriceEur)
                      : p.monthlyPriceEur;
                    const annualTotal = Math.round(annualMonthlyPrice(p.monthlyPriceEur) * 12);
                    return (
                      <>
                        <div className="mt-5 flex items-baseline gap-1">
                          <span className="text-4xl font-bold tracking-tight tabular-nums">
                            {displayPrice % 1 === 0
                              ? displayPrice
                              : formatEur(displayPrice).replace(",00", "")}
                            <span className="text-2xl">€</span>
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {displayPrice === 0 ? "" : t("pricing_section.per_month")}
                          </span>
                          {billing === "annual" && p.monthlyPriceEur > 0 && (
                            <span className="ml-1 text-xs text-muted-foreground/50 line-through tabular-nums">
                              {p.monthlyPriceEur}€
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                          {displayPrice === 0
                            ? t("pricing_section.no_card")
                            : billing === "annual"
                              ? (i18n.language === "en"
                                  ? `Billed ${annualTotal} € / year excl. VAT`
                                  : `Facturé ${annualTotal} € / an HT`)
                              : t("pricing_section.ht_per_month", { price: formatEur(priceHtFromTtc(p.monthlyPriceEur)) })}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {p.monthlyCredits === null
                            ? t("pricing_section.unlimited_credits")
                            : t("pricing_section.credits_per_month", { count: p.monthlyCredits.toLocaleString(locale) })}
                        </p>
                      </>
                    );
                  })()}
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {translated.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link href="/sign-up" className="mt-6 block">
                    <Button
                      size="lg"
                      variant={p.highlight ? "default" : "outline"}
                      className="w-full"
                    >
                      {translated.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </section>

        {/* Credit packs */}
        <section className="px-6 pb-12">
          <div className="mx-auto max-w-6xl">
            <div className="rounded-2xl border bg-muted/20 p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight">
                    {i18n.language === "en" ? "One-time credit packs" : "Packs de crédits ponctuels"}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                    {i18n.language === "en"
                      ? "Need a boost without changing plan? Buy extra credits to use at your own pace. Purchased credits are non-refundable once used."
                      : "Besoin d'un coup de pouce sans changer de forfait ? Achetez des crédits supplémentaires à utiliser à votre rythme. Les crédits achetés ne sont pas remboursables une fois utilisés."}
                  </p>
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                {PUBLIC_CREDIT_PACKS.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`pricing-pack-${c.id}`}
                    className="rounded-xl border bg-background p-4 text-center"
                  >
                    <p className="text-2xl font-bold tabular-nums">
                      {c.credits.toLocaleString(locale)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {i18n.language === "en" ? "credits" : "crédits"}
                    </p>
                    <p className="mt-2 text-lg font-semibold tabular-nums">
                      {c.priceEur} € {i18n.language === "en" ? "incl. VAT" : "TTC"}
                    </p>
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                      ({formatEur(priceHtFromTtc(c.priceEur))} € {i18n.language === "en" ? "excl. VAT" : "HT"})
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comparison table */}
        <section className="px-6 pb-16">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-bold tracking-tight text-center">
              {i18n.language === "en" ? "Compare plans" : "Comparer les forfaits"}
            </h2>
            <div className="mt-6 overflow-x-auto rounded-2xl border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left">
                    <th className="p-4 font-semibold">&nbsp;</th>
                    {PUBLIC_PLANS.map((p) => (
                      <th key={p.id} className="p-4 font-semibold text-center">
                        {planLabels[p.id]?.label ?? p.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((r, i) => (
                    <tr
                      key={r.label}
                      className={i % 2 === 0 ? "bg-background" : "bg-muted/10"}
                    >
                      <td className="p-4 font-medium">{r.label}</td>
                      {r.values.map((v, j) => (
                        <td
                          key={j}
                          className="p-4 text-center text-muted-foreground"
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="px-6 pb-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
              {t("faq_section.eyebrow")}
            </h2>
            <p className="mt-3 text-center text-muted-foreground text-sm md:text-base">
              {t("faq_section.subtitle")}
            </p>
            <Accordion
              type="single"
              collapsible
              className="mt-8 rounded-2xl border bg-card divide-y"
              data-testid="pricing-faq"
            >
              {translatedFaq.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-0 px-5"
                >
                  <AccordionTrigger className="text-base font-medium">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="px-6 pb-16">
          <div className="mx-auto max-w-3xl text-center text-sm text-muted-foreground">
            <p>
              {i18n.language === "en"
                ? <>Any questions before getting started?{" "}<Link href="/contact" className="text-primary hover:underline">Contact us</Link> — we reply within a few business hours.</>
                : <>Une question avant de vous lancer ?{" "}<Link href="/contact" className="text-primary hover:underline">Contactez-nous</Link> — nous répondons sous quelques heures ouvrées.</>}
            </p>
            <p className="mt-2">
              {i18n.language === "en"
                ? <>See also our{" "}<Link href="/legal/remboursement" className="text-primary hover:underline">refund policy</Link> and our{" "}<Link href="/legal/cgv" className="text-primary hover:underline">terms of sale</Link>.</>
                : <>Voir aussi notre{" "}<Link href="/legal/remboursement" className="text-primary hover:underline">politique de remboursement</Link> et nos{" "}<Link href="/legal/cgv" className="text-primary hover:underline">conditions générales de vente</Link>.</>}
            </p>
          </div>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
